export const DRIVER_ORDERS_CACHE_KEY = "driver_orders_cache_v1";
export const DRIVER_ORDER_DETAIL_CACHE_PREFIX = "driver_order_detail_cache_v1:";
export const DRIVER_STATUS_QUEUE_KEY = "driver_status_update_queue_v1";
export const DRIVER_STATUS_QUEUE_CHANGED_EVENT = "driver-status-queue-changed";
export const DRIVER_STATUS_QUEUE_MAX_ITEMS = 80;
export const DRIVER_STATUS_QUEUE_MAX_RETRY = 20;
export const DRIVER_STATUS_QUEUE_NON_RETRYABLE_STATUS = new Set([400, 401, 403, 404, 409, 422]);
let activeStatusQueueFlushPromise = null;

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Cache is best effort.
  }
}

export function saveCachedDriverOrders(orders) {
  writeJson(DRIVER_ORDERS_CACHE_KEY, {
    cached_at: new Date().toISOString(),
    orders: Array.isArray(orders) ? orders : [],
  });
}

export function loadCachedDriverOrders() {
  return readJson(DRIVER_ORDERS_CACHE_KEY, null);
}

export function saveCachedDriverOrderDetail(orderId, order) {
  if (!orderId || !order) return;
  writeJson(`${DRIVER_ORDER_DETAIL_CACHE_PREFIX}${orderId}`, {
    cached_at: new Date().toISOString(),
    order,
  });
}

export function loadCachedDriverOrderDetail(orderId) {
  if (!orderId) return null;
  return readJson(`${DRIVER_ORDER_DETAIL_CACHE_PREFIX}${orderId}`, null);
}

export function createDriverClientEventId(orderId, status) {
  const suffix = Math.random().toString(36).slice(2, 10);
  return `driver-ios-${orderId}-${status}-${Date.now()}-${suffix}`;
}

function readQueue() {
  const queue = readJson(DRIVER_STATUS_QUEUE_KEY, []);
  return Array.isArray(queue) ? queue : [];
}

function writeQueue(queue) {
  writeJson(DRIVER_STATUS_QUEUE_KEY, queue.slice(-DRIVER_STATUS_QUEUE_MAX_ITEMS));
  notifyDriverStatusQueueChanged();
}

function nowIso() {
  return new Date().toISOString();
}

function isNonRetryableQueueError(error) {
  const status = error?.response?.status;
  return DRIVER_STATUS_QUEUE_NON_RETRYABLE_STATUS.has(status);
}

function normalizeQueuedUpdate(update = {}) {
  const queuedAt = update.queued_at || nowIso();
  return {
    ...update,
    payload: update.payload || {},
    queue_status: update.queue_status || "queued",
    queued_at: queuedAt,
    updated_at: update.updated_at || queuedAt,
    last_attempt_at: update.last_attempt_at || null,
    retry_count: Number(update.retry_count || 0),
    last_error: update.last_error || "",
  };
}

export function notifyDriverStatusQueueChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(DRIVER_STATUS_QUEUE_CHANGED_EVENT, {
    detail: { count: readQueue().length },
  }));
}

export function queueDriverStatusUpdate(update) {
  const driverId = String(update?.driver_id || "").trim();
  if (!driverId) return null;
  const queue = readQueue();
  const nextUpdate = normalizeQueuedUpdate({
    ...update,
    driver_id: driverId,
    queue_status: "queued",
    queued_at: nowIso(),
    updated_at: nowIso(),
    last_attempt_at: null,
  });
  writeQueue([...queue.filter((item) => item.client_event_id !== nextUpdate.client_event_id), nextUpdate]);
  return nextUpdate;
}

export function getQueuedDriverStatusUpdates(driverId = "") {
  const normalizedDriverId = String(driverId || "").trim();
  return readQueue()
    .map(normalizeQueuedUpdate)
    .filter((item) => !normalizedDriverId || item.driver_id === normalizedDriverId);
}

export function getDriverStatusQueueSummary(driverId = "") {
  const queue = getQueuedDriverStatusUpdates(driverId);
  const retryable = queue.filter((item) => item.queue_status !== "failed_permanent");
  const permanentFailures = queue.filter((item) => item.queue_status === "failed_permanent");
  return {
    total: queue.length,
    retryable: retryable.length,
    permanentFailures: permanentFailures.length,
    oldestQueuedAt: queue[0]?.queued_at || "",
    newestQueuedAt: queue[queue.length - 1]?.queued_at || "",
  };
}

export function clearPermanentDriverStatusQueueFailures(driverId = "") {
  const normalizedDriverId = String(driverId || "").trim();
  const queue = readQueue().map(normalizeQueuedUpdate);
  const nextQueue = queue.filter((item) => (
    item.queue_status !== "failed_permanent"
    || (normalizedDriverId && item.driver_id !== normalizedDriverId)
  ));
  writeQueue(nextQueue);
  return getQueuedDriverStatusUpdates(normalizedDriverId).length;
}

export function flushDriverStatusQueue(updateOrderStatus, driverId = "") {
  if (activeStatusQueueFlushPromise) return activeStatusQueueFlushPromise;

  activeStatusQueueFlushPromise = (async () => {
    const normalizedDriverId = String(driverId || "").trim();
    const queue = readQueue().map(normalizeQueuedUpdate);
    const activeQueue = queue.filter((item) => !normalizedDriverId || item.driver_id === normalizedDriverId);
    if (!activeQueue.length || typeof updateOrderStatus !== "function") {
      return { flushed: 0, remaining: activeQueue.length, failedPermanent: 0 };
    }

    const snapshotEventIds = new Set(queue.map((item) => item.client_event_id));
    const remaining = [];
    let flushed = 0;
    let failedPermanent = 0;
    for (const item of queue) {
      if (normalizedDriverId && item.driver_id !== normalizedDriverId) {
        remaining.push(item);
        continue;
      }
      if (item.queue_status === "failed_permanent") {
        remaining.push(item);
        failedPermanent += 1;
        continue;
      }

      const attemptedAt = nowIso();
      try {
        await updateOrderStatus(item.order_id, item.status, item.payload);
        flushed += 1;
      } catch (error) {
        const retryCount = Number(item.retry_count || 0) + 1;
        const permanent = isNonRetryableQueueError(error) || retryCount >= DRIVER_STATUS_QUEUE_MAX_RETRY;
        if (permanent) {
          failedPermanent += 1;
        }
        remaining.push(normalizeQueuedUpdate({
          ...item,
          queue_status: permanent ? "failed_permanent" : "queued",
          retry_count: retryCount,
          last_attempt_at: attemptedAt,
          updated_at: attemptedAt,
          last_error: error?.response?.data?.detail || error?.message || "network_error",
          last_http_status: error?.response?.status || null,
        }));
      }
    }

    const newlyQueued = readQueue()
      .map(normalizeQueuedUpdate)
      .filter((item) => !snapshotEventIds.has(item.client_event_id));
    writeQueue([...remaining, ...newlyQueued]);
    return {
      flushed,
      remaining: getQueuedDriverStatusUpdates(normalizedDriverId).length,
      failedPermanent,
    };
  })().finally(() => {
    activeStatusQueueFlushPromise = null;
  });

  return activeStatusQueueFlushPromise;
}
